"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";

const TYPING_KEYS = ["t1", "t2"] as const;

const TYPE_MS = 82;
const DELETE_MS = 44;
const PAUSE_MS = 2000;

const PILLS = [
  { side: "l", index: 1, id: "amazon", rotate: 18, shift: 28 },
  { side: "l", index: 2, id: "mercado", rotate: -10, shift: 6 },
  { side: "l", index: 3, id: "temu", rotate: 14, shift: 32 },
  { side: "r", index: 1, id: "gemini", rotate: -18, shift: -28, accent: true },
  { side: "r", index: 2, id: "chatgpt", rotate: 10, shift: -6 },
  { side: "r", index: 3, id: "openclaw", rotate: -14, shift: -32 },
] as const;

const CHIPS = ["analysis", "image", "video"] as const;

const MIN_SCALE = 0.8;
const GRID_LIFT_PX = 50;
const GRID_MARGIN_START = 240;
const GRID_MARGIN_END = 60;

const REVEAL_END = 0.32;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function useTypedText(lines: readonly string[]): string {
  const [text, setText] = useState("");

  useEffect(() => {
    let lineIndex = 0;
    let charIndex = 0;
    let deleting = false;
    let timer: ReturnType<typeof setTimeout>;

    const step = () => {
      const line = lines[lineIndex];

      if (!deleting) {
        if (charIndex < line.length) {
          charIndex += 1;
          setText(line.slice(0, charIndex));
          timer = setTimeout(step, TYPE_MS);
          return;
        }

        timer = setTimeout(() => {
          deleting = true;
          step();
        }, PAUSE_MS);
        return;
      }

      if (charIndex > 0) {
        charIndex -= 1;
        setText(line.slice(0, charIndex));
        timer = setTimeout(step, DELETE_MS);
        return;
      }

      deleting = false;
      lineIndex = (lineIndex + 1) % lines.length;
      timer = setTimeout(step, TYPE_MS);
    };

    step();

    return () => clearTimeout(timer);
  }, [lines]);

  return text;
}

type HeroProps = {
  brand: string;

  ctaHref: string;
  title: string;
  subtitle: string;
};

export function HomeHero({ ctaHref, title, subtitle, brand }: HeroProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const pillRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const t = useTranslations("site.homePage.hero");
  const typingLines = useMemo(
    () => TYPING_KEYS.map((key) => t(`typing.${key}`)),
    [t],
  );
  const typed = useTypedText(typingLines);

  useEffect(() => {
    const section = sectionRef.current;
    const grid = gridRef.current;
    const bg = bgRef.current;

    if (!section || !grid || !bg) {
      return;
    }

    const wide = window.matchMedia("(min-width: 992px)");
    let scheduled = false;

    const apply = () => {
      scheduled = false;

      if (!wide.matches) {
        return;
      }

      const runway = Math.max(1, section.scrollHeight - window.innerHeight);
      const top = section.getBoundingClientRect().top + window.scrollY;
      const y = window.scrollY;
      const progress = clamp((y - top) / runway, 0, 1);
      const shrink =
        progress <= REVEAL_END ? 0 : (progress - REVEAL_END) / (1 - REVEAL_END);

      bg.style.opacity = String(progress);
      grid.style.transform = `translateY(${-GRID_LIFT_PX * shrink}px) scale(${
        1 - shrink * (1 - MIN_SCALE)
      })`;
      grid.style.marginTop = `${
        GRID_MARGIN_START - progress * (GRID_MARGIN_START - GRID_MARGIN_END)
      }px`;

      PILLS.forEach((pill, index) => {
        const node = pillRefs.current[index];

        if (node) {
          node.style.transform = `rotate(${pill.rotate * (1 - shrink)}deg) translateX(${
            pill.shift * (1 - shrink)
          }px)`;
        }
      });

      hintRef.current?.classList.toggle(
        "shouye-banana__scroll-hint--away",
        Math.max(0, y - top) > 32,
      );
    };

    const reset = () => {
      bg.style.opacity = "";
      grid.style.transform = "";
      grid.style.marginTop = "";
      hintRef.current?.classList.remove("shouye-banana__scroll-hint--away");

      for (const node of pillRefs.current) {
        if (node) {
          node.style.transform = "";
        }
      }

      scheduled = false;
    };

    const schedule = () => {
      if (!wide.matches || scheduled) {
        return;
      }

      scheduled = true;
      requestAnimationFrame(apply);
    };

    const onModeChange = () => {
      if (wide.matches) {
        schedule();
      } else {
        reset();
      }
    };

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", onModeChange);
    wide.addEventListener("change", onModeChange);
    onModeChange();

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", onModeChange);
      wide.removeEventListener("change", onModeChange);
    };
  }, []);

  return (
    <section className="shouye-banana" aria-label={t("ariaLabel")} ref={sectionRef}>
      <div className="w1440 shouye-banana__frame">
        <div className="shouye-banana__zoom-shell">
          <div
            className="shouye-banana__zoom-shell-bg"
            aria-hidden="true"
            ref={bgRef}
          />
          <div className="shouye-banana__grid" ref={gridRef}>
            {(["l", "r"] as const).map((side) => (
              <div
                key={side}
                className={`shouye-banana__col shouye-banana__col--${
                  side === "l" ? "left" : "right"
                }`}
                aria-hidden="true"
              >
                {PILLS.filter((pill) => pill.side === side).map((pill) => (
                  <span key={pill.id} className="shouye-banana__fan">
                    <span
                      className={`shouye-banana__pill shouye-banana__pill--fan-${pill.side}${pill.index}${
                        "accent" in pill && pill.accent
                          ? " shouye-banana__pill--accent"
                          : ""
                      }`}
                      ref={(node) => {
                        pillRefs.current[PILLS.indexOf(pill)] = node;
                      }}
                    >
                      {t(`pills.${pill.id}`)}
                    </span>
                  </span>
                ))}
              </div>
            ))}

            <div className="shouye-banana__col shouye-banana__col--center">
              <div className="shouye-banana__main">
                <div className="shouye-banana__headline">
                  <h2 className="shouye-banana__title">
                    <span className="shouye-banana__title-desktop">
                      <span className="shouye-banana__title-line shouye-banana__title-line--hero">
                        <span className="shouye-banana__title-line--grad">
                          {title}
                        </span>
                        <span className="shouye-banana__title-line--grad">
                          {subtitle}
                        </span>
                      </span>
                    </span>
                  </h2>
                  <div className="shouye-banana__robot-wrap">
                    {/* 纯装饰插画，随模板走 public，不进上传库 */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      className="shouye-banana__robot"
                      src="/images/home/1-2.png"
                      alt=""
                    />
                  </div>
                </div>

                <a
                  href={ctaHref}
                  className="shouye-banana__panel-anchor"
                  aria-label={t("ctaAria", { brand })}
                >
                  <div className="shouye-banana__panel-shell">
                    <div className="shouye-banana__panel">
                      <div className="shouye-banana__panel-typing-bar">
                        <div className="shouye-banana__placeholder" aria-hidden="true">
                          <span className="shouye-banana__typing-line">
                            <span className="shouye-banana__typing-text">
                              {typed}
                            </span>
                            <span className="shouye-banana__typing-caret" />
                          </span>
                        </div>
                        <span className="shouye-banana__panel-fab" aria-hidden="true">
                          ↑
                        </span>
                      </div>
                      <div className="shouye-banana__panel-row">
                        <div className="shouye-banana__chips">
                          {CHIPS.map((chip) => (
                            <span key={chip} className="shouye-banana__outline">
                              {t(`chips.${chip}`)}
                            </span>
                          ))}
                        </div>
                        <span className="shouye-banana__cta">{t("cta")}</span>
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="shouye-banana__scroll-hint" ref={hintRef}>
          <span className="shouye-banana__scroll-text">{t("scrollHint")}</span>
          <span className="shouye-banana__scroll-chevron" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
}
